import { useState, useEffect } from 'react';

/**
 * A custom hook to persist form state to localStorage.
 * Handles complex objects and automatically retrieves saved data.
 *
 * @param {string} key - A unique key for the page/form (e.g., 'invoiceForm', 'inventoryEntry').
 * @param {Object} initialState - The default state when no saved data exists or after reset.
 * @param {boolean} [active=true] - A flag to enable or disable auto-saving (useful for modals).
 * @returns {Array} - [state, setState, resetState, reloadState]
 */
export const usePersistedForm = (key, initialState, active = true) => {
    // Initialize state, loading from localStorage if the key exists
    const [state, setState] = useState(() => {
        try {
            const savedState = localStorage.getItem(key);
            if (savedState) {
                // Merge initial state with saved data so any new fields in code still exist
                return { ...initialState, ...JSON.parse(savedState) };
            }
        } catch (error) {
            console.error(`Error reading ${key} from localStorage`, error);
        }
        return initialState;
    });

    // Auto-save the state to localStorage whenever it changes
    useEffect(() => {
        if (!active) return;
        
        try {
            // JSON.stringify can handle complex nesting (lists, objects) automatically
            // Be mindful: if state contains File objects, they won't serialize properly 
            // and might need to be filtered out before saving.
            localStorage.setItem(key, JSON.stringify(state));
        } catch (error) {
            console.error(`Error saving ${key} to localStorage`, error);
        }
    }, [key, state, active]);

    // Function to wipe local storage and reset the state (used upon successful submission)
    const resetState = () => {
        try {
            localStorage.removeItem(key);
        } catch (error) {
            console.error(`Error removing ${key} from localStorage`, error);
        }
        setState(initialState);
    };

    // Function to reload the state from localStorage (useful if state was temporarily overwritten, e.g., by edit modal)
    const reloadState = () => {
        try {
            const savedState = localStorage.getItem(key);
            if (savedState) {
                setState({ ...initialState, ...JSON.parse(savedState) });
                return;
            }
        } catch (error) {
            console.error(`Error reading ${key} from localStorage`, error);
        }
        setState(initialState);
    };

    return [state, setState, resetState, reloadState];
};

export default usePersistedForm;
